/**
 * Traduce la ruta técnica de un cambio al nombre que la persona ve en el formulario.
 *
 * ── POR QUÉ ESTO DECIDE SI EL HISTORIAL SIRVE ────────────────────────────────
 *
 * El diff del servidor habla en rutas del documento: `negotiations.paymentAgreement.totalAmount`.
 * Mostrado así, el historial es legible sólo para quien conoce el esquema de Mongo — o sea, para
 * nadie del negocio. Y un historial que no se entiende no se mira, con lo cual da lo mismo
 * tenerlo.
 *
 * ── POR QUÉ UN MAPA A MANO Y NO ALGO DERIVADO ────────────────────────────────
 *
 * La tentación es derivarlo del diccionario de campos de los formularios. No alcanza: ese
 * diccionario cubre el formulario del PORTAL, y acá viajan campos que nunca estuvieron en un
 * formulario —`orderSAP`, `isSendSAP`, `currentState`— más los del documento interno. Un mapa
 * explícito además deja decir «Asesor asignado» donde el campo se llama `advisor`, que es lo
 * que la gente busca cuando abre el historial.
 *
 * Lo que no está en el mapa NO se oculta: se muestra la última parte de la ruta. Un historial
 * incompleto que igual dice algo es mejor que uno que esconde lo que no supo nombrar.
 */

/** Prefijos de colección que no aportan nada al leer: la entidad ya está en el contexto. */
const COLLECTION_PREFIXES = ['clients.', 'negotiations.', 'personindividuals.', 'personjuridicals.']

const FIELD_LABELS: Record<string, string> = {
  // ── Comunes ──
  advisor: 'Asesor asignado',
  company: 'Empresa',
  agency: 'Agencia',
  society: 'Sociedad',
  project: 'Proyecto',
  property: 'Inmueble',
  currency: 'Moneda',
  riskLevel: 'Nivel de riesgo',
  place: 'Ubicación',
  deleted: 'Estado de eliminación',

  // ── Negociación ──
  owners: 'Propietarios',
  code: 'Código',
  currentState: 'Estado actual',
  history: 'Historial de estados',
  workflow: 'Flujo',
  orderSAP: 'Orden SAP',
  isSendSAP: 'Envío a SAP',
  productionOrder: 'Orden de producción',
  details: 'Origen de fondos',
  referralNegotiation: 'Referido',
  extendedDueDiligence: 'Debida diligencia ampliada',
  ubicationNegotiation: 'Ubicación de la negociación',
  contactMethod: 'Medio de contacto',

  // ── Acuerdo de pago ──
  paymentAgreement: 'Acuerdo de pago',
  totalAmount: 'Valor total',
  reserve: 'Reserva',
  payments: 'Cuotas',
  deliveryDate: 'Fecha de entrega',
  methodPayment: 'Forma de pago',
  sourceFundsForPayment: 'Origen de los fondos',
  titleAmountWithoutTax: 'Valor del título sin impuestos',
  totalAmountWithoutTax: 'Valor total sin impuestos',
  upgradesAmountWithoutTax: 'Valor de modificaciones sin impuestos',
  propertyAmountWithoutTax: 'Valor del inmueble sin impuestos',

  // ── Cliente ──
  applicant: 'Datos del titular',
  pep: 'Persona expuesta políticamente',
  economicInformation: 'Información económica',
  equityStatement: 'Estado patrimonial',
  legalRepresentatives: 'Representantes legales',
  contract: 'Contrato',
  typePerson: 'Tipo de persona',
  fullname: 'Nombre completo',
  nit: 'NIT',
  photo: 'Fotografía',
  identificationCard: 'Documento de identidad'
}

/**
 * El nombre legible de una ruta de cambio.
 *
 * Traduce CADA segmento que conoce y deja los demás como están, así
 * `negotiations.paymentAgreement.totalAmount` se lee «Acuerdo de pago › Valor total» y un campo
 * que nadie mapeó todavía se lee por su último tramo en vez de desaparecer.
 */
export function labelForPath(path: string): string {
  const withoutCollection = COLLECTION_PREFIXES.reduce(
    (value, prefix) => (value.startsWith(prefix) ? value.slice(prefix.length) : value),
    path
  )

  const segments = withoutCollection
    .split('.')
    // Los índices de array no se muestran: el diff compara arrays como unidad, así que un
    // número acá sería un resto de una ruta que no debería haberse generado.
    .filter((segment) => !/^\d+$/.test(segment))

  if (segments.length === 0) return path

  return segments.map((segment) => FIELD_LABELS[segment] ?? segment).join(' › ')
}

/**
 * Un valor del diff, en texto.
 *
 * No intenta pintar objetos ni listas: dice QUÉ tipo de cosa cambió y cuántos elementos tiene.
 * «3 elementos» responde la pregunta útil —cambió la lista de propietarios— sin volcar un JSON
 * que nadie va a leer en una fila de timeline.
 */
export function formatVersionValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) {
    return value.length === 1 ? '1 elemento' : `${value.length} elementos`
  }
  if (typeof value === 'object') return 'Varios campos'
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  return String(value)
}
